import React from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Box,
  Button,
  Container,
  Flex,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  SimpleGrid,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import { useGameDetails, type TabularSection } from '../hooks/useGameScores';
import { getPstTime } from '../lib/pstTime';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { getTeamInfoById } from '../utils/getTeamInfo';

const SCOREBOARD_TITLE = 'ScoreBoard';

function scoreBoardLineScoreCellProps(isScoreBoard: boolean, col: string) {
  const isRunsCol = isScoreBoard && String(col).toLowerCase() === 'r';
  const border = isRunsCol
    ? {
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid' as const,
        borderLeftColor: 'gray.500',
      }
    : {};
  if (!isScoreBoard || col === 'team') return border;
  return {
    textAlign: 'center' as const,
    ...border,
  };
}

type RenderOptions = {
  sectionKey?: string;
  expandable?: boolean;
  expandedKeys?: Record<string, boolean>;
  onToggleRow?: (key: string) => void;
};

const renderSectionTable = (
  section: TabularSection | undefined,
  columnsOverride?: string[],
  size: 'sm' | 'xs' = 'sm',
  options?: RenderOptions
) => {
  if (!section || !section.rows.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        No data.
      </Text>
    );
  }

  const isScoreBoard = section.title === SCOREBOARD_TITLE;
  const isBatterSection = section.title.includes('Batters');
  const isPitcherSection = section.title.includes('Pitchers');

  // Drop Position column for batter sections (we'll fold it into the Name display)
  const baseColumns = columnsOverride && columnsOverride.length > 0 ? columnsOverride : section.columns;
  const columns = isBatterSection ? baseColumns.filter((c) => c !== 'Position') : baseColumns;

  // Build totals row for batter/pitcher sections (numeric columns only)
  let rows = section.rows;
  if (isBatterSection || isPitcherSection) {
    const totals: Record<string, unknown> = {};
    for (const col of columns) {
      if (col === 'Name') {
        totals[col] = 'Totals';
        continue;
      }
      if (col === 'IP') {
        continue;
      }
      const sum = rows.reduce((acc, row) => {
        const v = Number((row as any)[col]);
        return Number.isFinite(v) ? acc + v : acc;
      }, 0);
      if (Number.isFinite(sum)) {
        totals[col] = sum;
      }
    }
    rows = [...rows, totals];
  }

  return (
    <Table size={size} variant="simple">
      <Thead>
        <Tr>
          {columns.map((col) => (
            <Th
              key={col}
              textAlign={
                isScoreBoard && col !== 'team'
                  ? 'center'
                  : (isBatterSection || isPitcherSection) && col !== 'Name'
                    ? 'center'
                    : 'left'
              }
              {...scoreBoardLineScoreCellProps(isScoreBoard, col)}
            >
              {isScoreBoard && col === 'team' ? '' : col}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row, idx) => (
          (() => {
            const rowKey = options?.sectionKey ? `${options.sectionKey}-${idx}` : `${section.title}-${idx}`;
            const isTotalsRow = (isBatterSection || isPitcherSection) && String((row as any).Name) === 'Totals';
            const isExpanded = !!options?.expandedKeys?.[rowKey];
            const isClickable = options?.expandable && !isTotalsRow;

            const mainRow = (
              <Tr
                key={rowKey}
                cursor={isClickable ? 'pointer' : 'default'}
                onClick={isClickable && options?.onToggleRow ? () => options.onToggleRow!(rowKey) : undefined}
              >
                {columns.map((col) => {
                  const rawValue = (row as any)[col];

                  if (isBatterSection && col === 'Name') {
                    const name = String((row as any).Name ?? '');
                    const pos = String((row as any).Position ?? '');
                    const parts = name.trim().split(/\s+/);
                    const first = parts[0] || '';
                    const last = parts.length > 1 ? parts[parts.length - 1] : '';
                    const firstInitial = first ? `${first[0]}.` : '';
                    const displayName = last ? `${firstInitial} ${last}`.trim() : name;
                    const withPos = pos ? `${displayName} (${pos})` : displayName;
                    const isStarter = (row as any).is_starter !== false;
                    if (!isStarter && !isTotalsRow) {
                      return (
                        <Td key={col} {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                          <Text as="span" pl={4}>
                            * {withPos}
                          </Text>
                        </Td>
                      );
                    }
                    return (
                      <Td key={col} {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                        {withPos}
                      </Td>
                    );
                  }

                  const value = String(rawValue ?? '');
                  return (
                    <Td
                      key={col}
                      textAlign={
                        isScoreBoard && col !== 'team'
                          ? 'center'
                          : (isBatterSection || isPitcherSection) && col !== 'Name'
                            ? 'center'
                            : 'left'
                      }
                      {...scoreBoardLineScoreCellProps(isScoreBoard, col)}
                    >
                      {value}
                    </Td>
                  );
                })}
              </Tr>
            );

            if (!isBatterSection && !isPitcherSection) {
              return mainRow;
            }

            const extraRow =
              isExpanded && !isTotalsRow ? (
                <Tr key={`${rowKey}-details`}>
                  {columns.map((col) => {
                    const isNameCol = col === 'Name';
                    const baseAlign = (isBatterSection || isPitcherSection) && !isNameCol ? 'center' : 'left';

                    if (isNameCol) {
                      return (
                        <Td key={col} textAlign="left" bg="gray.800" {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                          <Text fontSize="xs" color="gray.300">
                            Game dates: 4/08/26, 4/10/26
                          </Text>
                        </Td>
                      );
                    }

                    const showActualForCol = col === 'H' || col === 'R';
                    const simulated = Number((row as any)[col]);
                    const actual = showActualForCol && Number.isFinite(simulated) ? simulated + 1 : '';

                    return (
                      <Td key={col} textAlign={baseAlign} bg="gray.800" {...scoreBoardLineScoreCellProps(isScoreBoard, col)}>
                        <Text fontSize="xs" color="gray.400">
                          {actual !== '' ? actual : ''}
                        </Text>
                      </Td>
                    );
                  })}
                </Tr>
              ) : null;

            return (
              <React.Fragment key={`${rowKey}-frag`}>
                {mainRow}
                {extraRow}
              </React.Fragment>
            );
          })()
        ))}
      </Tbody>
    </Table>
  );
};

const GameScoreDetailsPage: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const myLeague = useMyLeagueStore((state) => state.myLeague);
  const bgColor = useColorModeValue('white', 'gray.800');
  const sectionTitleColor = useColorModeValue('gray.700', 'gray.200');
  const extrasLineColor = useColorModeValue('gray.600', 'gray.400');

  const numericGameId = gameId ? Number(gameId) : undefined;
  const { data, isLoading } = useGameDetails(numericGameId, !!numericGameId);

  const sections = data?.sections ?? [];
  const game = (data?.game ?? null) as Record<string, unknown> | null;

  const scoreBoard = sections.find((s) => s.title === SCOREBOARD_TITLE) ?? sections[0];
  const pitchingDecisions = sections.find((s) => s.title === 'Pitching Decisions');
  const awayBatters = sections.find((s) => s.title === 'Away Batters');
  const homeBatters = sections.find((s) => s.title === 'Home Batters');
  const awayPitchers = sections.find((s) => s.title === 'Away Pitchers');
  const homePitchers = sections.find((s) => s.title === 'Home Pitchers');
  const awayBattersExtra = sections.find((s) => s.title === 'Away Batters Extra');
  const homeBattersExtra = sections.find((s) => s.title === 'Home Batters Extra');
  const awayPitchersExtra = sections.find((s) => s.title === 'Away Pitchers Extra');
  const homePitchersExtra = sections.find((s) => s.title === 'Home Pitchers Extra');

  const pitchingDecisionsLine = pitchingDecisions?.rows?.length
    ? pitchingDecisions.rows
        .map((row) => `${String(row.Stat ?? '')}: ${String(row.Value ?? '')}`)
        .join(' • ')
    : '';

  const [expandedRows, setExpandedRows] = React.useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = React.useState(false);

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const awayTeamId = (game?.away_team_id as number | undefined) ?? undefined;
  const homeTeamId = (game?.home_team_id as number | undefined) ?? undefined;
  const leagueForTeams = (myLeague as Parameters<typeof getTeamInfoById>[1] | null) ?? null;

  const awayTeamInfo = awayTeamId && leagueForTeams ? getTeamInfoById(awayTeamId, leagueForTeams) : null;
  const homeTeamInfo = homeTeamId && leagueForTeams ? getTeamInfoById(homeTeamId, leagueForTeams) : null;

  const handleToggleAll = () => {
    if (allExpanded) {
      setExpandedRows({});
      setAllExpanded(false);
      return;
    }

    const next: Record<string, boolean> = {};

    const addSectionKeys = (section: TabularSection | undefined, sectionKey: string) => {
      if (!section || !section.rows.length) return;
      const isBatOrPitch = section.title.includes('Batters') || section.title.includes('Pitchers');
      const baseCount = section.rows.length;
      const totalRows = isBatOrPitch ? baseCount + 1 : baseCount; // +1 for totals row
      const limit = isBatOrPitch ? totalRows - 1 : totalRows; // skip totals row
      for (let i = 0; i < limit; i++) {
        next[`${sectionKey}-${i}`] = true;
      }
    };

    addSectionKeys(awayBatters, 'away-batters');
    addSectionKeys(awayPitchers, 'away-pitchers');
    addSectionKeys(homeBatters, 'home-batters');
    addSectionKeys(homePitchers, 'home-pitchers');

    setExpandedRows(next);
    setAllExpanded(true);
  };

  if (!gameId) {
    return (
      <Container maxW="container.xl" py={8}>
        <PageTitle mb={4}>Game Details</PageTitle>
        <Text color="gray.500" mb={4}>
          Missing game ID.
        </Text>
        <Button onClick={() => navigate('/scoreboard')} colorScheme="blue">
          Back to Scoreboard
        </Button>
      </Container>
    );
  }

  const scheduled = (game?.scheduled_date as string | undefined) || undefined;
  const pstTimestamp = scheduled ? getPstTime(scheduled) : undefined;
  const dateLabel = pstTimestamp
    ? new Date(pstTimestamp.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

  const awayName = awayTeamInfo?.name || '';
  const homeName = homeTeamInfo?.name || '';
  const type = (game?.type as string) || '';
  const typeSuffix = type && type !== 'Season' ? ` (${type})` : '';

  let title = 'Game Details';
  const detailParts: string[] = [];
  if (dateLabel) detailParts.push(dateLabel);
  if (awayName || homeName) detailParts.push(`${awayName || 'Away'} at ${homeName || 'Home'}`);
  if (detailParts.length) {
    title += `: ${detailParts.join(' ')}${typeSuffix}`;
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} mb={6}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/scoreboard" color="blue.500">
            Scoreboard
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <Text color="gray.500">Game Score Details</Text>
        </BreadcrumbItem>
      </Breadcrumb>

      <PageTitle mb={6}>{title}</PageTitle>

      {isLoading ? (
        <Flex justify="center" py={8}>
          <Spinner size="lg" />
        </Flex>
      ) : (
        <>
          {/* Line score */}
          <Box mb={6} bg={bgColor} borderRadius="md" p={4}>
            {renderSectionTable(scoreBoard)}
            {pitchingDecisionsLine && (
              <Box mt={2} textAlign="left" pl={4}>
                <Text fontSize="sm" color={extrasLineColor} textAlign="left">
                  {pitchingDecisionsLine}
                </Text>
              </Box>
            )}
          </Box>

          {/* Detailed batters and pitchers: four stacked tables */}
          <VStack align="stretch" spacing={4}>
            <Box bg={bgColor} borderRadius="md" p={4}>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="bold" color={sectionTitleColor}>
                  {(awayTeamInfo?.name || 'Away') + ' Batters'}
                </Text>
                <Text
                  as="button"
                  fontSize="xs"
                  color="blue.300"
                  _hover={{ textDecoration: 'underline', color: 'blue.200' }}
                  onClick={handleToggleAll}
                  display="none"
                >
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </Text>
              </Flex>
              {renderSectionTable(awayBatters, undefined, 'xs', {
                sectionKey: 'away-batters',
                expandable: false,
              })}
              {awayBattersExtra && awayBattersExtra.rows.length > 0 && (
                <Box mt={2} textAlign="left">
                  {awayBattersExtra.rows.map((row, idx) => (
                    <Text key={idx} fontSize="sm" color={extrasLineColor} textAlign="left">
                      {String(row.Stat ?? '')}: {String(row.Value ?? '')}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>

            <Box bg={bgColor} borderRadius="md" p={4}>
              <Text fontWeight="bold" color={sectionTitleColor} mb={2}>
                {(awayTeamInfo?.name || 'Away') + ' Pitchers'}
              </Text>
              {renderSectionTable(awayPitchers, undefined, 'xs', {
                sectionKey: 'away-pitchers',
                expandable: false,
              })}
              {awayPitchersExtra && awayPitchersExtra.rows.length > 0 && (
                <Box mt={2} textAlign="left">
                  {awayPitchersExtra.rows.map((row, idx) => (
                    <Text key={idx} fontSize="sm" color={extrasLineColor} textAlign="left">
                      {String(row.Stat ?? '')}: {String(row.Value ?? '')}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>

            <Box bg={bgColor} borderRadius="md" p={4}>
              <Text fontWeight="bold" color={sectionTitleColor} mb={2}>
                {(homeTeamInfo?.name || 'Home') + ' Batters'}
              </Text>
              {renderSectionTable(homeBatters, undefined, 'xs', {
                sectionKey: 'home-batters',
                expandable: false,
              })}
              {homeBattersExtra && homeBattersExtra.rows.length > 0 && (
                <Box mt={2} textAlign="left">
                  {homeBattersExtra.rows.map((row, idx) => (
                    <Text key={idx} fontSize="sm" color={extrasLineColor} textAlign="left">
                      {String(row.Stat ?? '')}: {String(row.Value ?? '')}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>

            <Box bg={bgColor} borderRadius="md" p={4}>
              <Text fontWeight="bold" color={sectionTitleColor} mb={2}>
                {(homeTeamInfo?.name || 'Home') + ' Pitchers'}
              </Text>
              {renderSectionTable(homePitchers, undefined, 'xs', {
                sectionKey: 'home-pitchers',
                expandable: false,
              })}
              {homePitchersExtra && homePitchersExtra.rows.length > 0 && (
                <Box mt={2} textAlign="left">
                  {homePitchersExtra.rows.map((row, idx) => (
                    <Text key={idx} fontSize="sm" color={extrasLineColor} textAlign="left">
                      {String(row.Stat ?? '')}: {String(row.Value ?? '')}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>
          </VStack>

          {numericGameId != null && (
            <Text mt={4} color="gray.500" fontSize="sm">
              Game ID: {numericGameId}
            </Text>
          )}
        </>
      )}
    </Container>
  );
};

export default GameScoreDetailsPage;

